import { endpoints } from '@/services/endpoints'
import { get, post } from '@/services/http'
import { ERROR_CODES } from '@/services/apiError'
/* Every timestamp this API returns is naive UTC, and `new Date()` reads one as
   LOCAL — a five-and-a-half-hour error in IST, in the direction that makes a turn
   from a minute ago look hours old. `toInstant` restores the designator, guarded
   against the day the backend starts sending one. It lives under anomalies because
   that is where the problem was first hit; it is general, and copying it here to
   avoid the import would be how the two drift. */
import { toInstant } from '@/features/anomalies/api'

/**
 * The conversations a person has with the copilot.
 *
 * ── One global chat, not one per record ───────────────────────────────────
 * The dock used to keep a session per anomaly, found again by searching session
 * titles for the anomaly id. That is gone: there is one conversation in hand at a
 * time, it follows the reader across every module, and what a question is about
 * travels on the TURN, in `metadata.mentions`, not in which session it lands in.
 *
 * ── Sessions are private to the person who made them ─────────────────────
 * `list_sessions` scopes on `created_by == user.id`, not just the account. The list
 * is "your chats", never the team's.
 *
 * ── Lazily, on the first question ─────────────────────────────────────────
 * "New chat" creates nothing. There is no endpoint that can delete a session, so an
 * eager create would leave an empty, undeletable conversation in the list every
 * time somebody opened the dock and closed it again.
 *
 * ── No rename, no delete ──────────────────────────────────────────────────
 * The API has `POST` and `GET` on sessions and nothing else. Both are recorded in
 * BACKEND-GAPS.md; the list offers neither until the routes exist.
 */

/** How long the server tolerates silence on a turn before it reaps it. Its own sweep constant. */
export const SWEEP_MS = 300_000

/** `CopilotSession.title` is `String(255)`. */
const TITLE_MAX = 255
/** Long enough to tell chats apart in a 360px list; the full question is in the transcript. */
const TITLE_SOFT = 80

/**
 * A session's title, from the question that opened it.
 *
 * The first line only, whitespace collapsed, cut at a word near 80 characters. The
 * list truncates visually anyway; this keeps a pasted paragraph from becoming a
 * 255-character title nobody can scan.
 */
export function titleFromQuestion(question) {
  const line = String(question ?? '')
    .split('\n')
    .map((part) => part.trim())
    .find(Boolean)
  if (!line) return null
  const flat = line.replace(/\s+/g, ' ')
  if (flat.length <= TITLE_SOFT) return flat
  const cut = flat.slice(0, TITLE_SOFT)
  const space = cut.lastIndexOf(' ')
  return `${(space > 40 ? cut.slice(0, space) : cut).trimEnd()}…`.slice(0, TITLE_MAX)
}

/** `title` is nullable but the KEY is required — an omitted one is a 400, not a default. */
export function createCopilotSession(title, signal) {
  return post(endpoints.copilot.sessions, { title: title ?? null }, { signal })
}

/** An untitled chat — the Copilot screen and `scripts/verify-copilot-live.mjs` use it. */
export const newCopilotSession = (signal) => createCopilotSession(null, signal)

/** One page of this person's sessions, most recently active first. */
export async function fetchCopilotSessions({ page = 1, pageSize = 20 } = {}, signal) {
  const body = await get(endpoints.copilot.sessions, {
    signal,
    params: { sort_by: 'updated_at', sort_dir: 'desc', page, page_size: pageSize },
  })
  return {
    sessions: body?.sessions ?? [],
    page: body?.page ?? page,
    totalPages: body?.total_pages ?? 1,
  }
}

/**
 * Whether a refused turn was refused because the session already has one pending.
 *
 * The server says so with a **400**, not a 409: `create_turn` raises
 * `ValidationAppError("A response is already pending for this session.",
 * {"session_id": …})`. This client tested for a 409 and so never recognised it —
 * the recovery below never ran, and one failed turn left the conversation
 * refusing every question after it.
 *
 * A bare 400 is also an ordinary validation failure, so the `session_id` reason is
 * what identifies this one. 409 is still accepted in case the server moves to it.
 */
export function isPendingTurnRefusal(error) {
  if (error?.code === ERROR_CODES.CONFLICT) return true
  return error?.code === ERROR_CODES.VALIDATION && Boolean(error?.fields?.session_id)
}

/**
 * Whether a question without an answer can still produce one.
 *
 * `error` never will: a failed turn writes no reply row, so the session refuses
 * every later question, for ever. `streaming` past the sweep will not either — the
 * server reaps a turn after 300 seconds of silence and never marks it.
 *
 * Measured against turn AGE rather than the silence the server measures, which is
 * conservative in the wrong direction for an analysis still running. That is the
 * right way round to be wrong: a turn wrongly called dead costs a new chat, while
 * one wrongly called live costs a panel that hangs until the sweep fires.
 */
export function pendingTurnState(turn) {
  if (!turn || turn.status === 'done') return 'none'
  if (turn.status === 'error') return 'dead'
  const startedAt = Date.parse(toInstant(turn.created_at) ?? '')
  const dead = Number.isFinite(startedAt) && Date.now() - startedAt >= SWEEP_MS
  return dead ? 'dead' : 'attachable'
}

/** The session's newest question and whether it can still be answered. */
export async function findPendingTurn(sessionId, signal) {
  const body = await get(endpoints.copilot.sessionTurns(sessionId), {
    signal,
    params: { role: 'user', sort_dir: 'desc', page_size: 1 },
  })
  const turn = body?.turns?.[0] ?? null
  return { turn, state: pendingTurnState(turn) }
}
